/**
 * IS THE CHOSEN SUPPORT AT HOME PLAN STILL FOR THIS HOUSEHOLD? (25 Oct 2026)
 *
 * A plan chosen with both at home ("Spend $416,000 on the home…", $4,379,840) stayed on Home as the chosen plan,
 * at that figure, after John's aged care plan was carried in — when the plans tested for the household as it is
 * now did not include it at all. The household a plan was priced on is the aged care plan carried with it (none,
 * or which one); that is recorded when it is chosen (`inputs.householdFingerprint`, the same key the plans page
 * checks its own run against) and compared here with the household now, so Home, the plan page and step 3 say the
 * same thing without re-testing anything.
 */
import { chosenAgedCarePlanOf, sahEnteringPerson, sahMoveAnswer } from './sahEngineContext.ts';
import { outOfDateOf } from '../../utils/chosenPlanRefresh.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';

/** The household key: the aged care plan carried in, by id, or none. */
export const sahHouseholdFingerprint = (agedCarePlanId: string | null): string => JSON.stringify(['whole-aged-care-plan', agedCarePlanId]);

/** Plans chosen before 24 Oct carried only the deposit of a linked aged care plan, never the whole plan. */
const BEFORE_WHOLE_PLAN = 'before-whole-plan';

/** The household now: the chosen aged care plan when the profile says the partner is moving, else none. */
export function sahHouseholdNow(factFind: DraftFactFind | null | undefined, savedStrategies: ReadonlyArray<SavedStrategy> | null | undefined): string {
    const ac = sahMoveAnswer(factFind) === 'yes' ? chosenAgedCarePlanOf(savedStrategies) : null;
    return sahHouseholdFingerprint(ac?.id != null ? String(ac.id) : null);
}

/** The household a saved plan was priced on. */
export function sahHouseholdOf(plan: SavedStrategy | null | undefined): string {
    const inputs = (plan?.inputs || {}) as { householdFingerprint?: unknown; agedCareContext?: unknown };
    if (typeof inputs.householdFingerprint === 'string') return inputs.householdFingerprint;
    return inputs.agedCareContext === 'linked' ? BEFORE_WHOLE_PLAN : sahHouseholdFingerprint(null);
}

const carried = (fingerprint: string): string | null => {
    try { const v = JSON.parse(fingerprint) as unknown; return Array.isArray(v) && v[1] != null ? String(v[1]) : null; } catch { return fingerprint === BEFORE_WHOLE_PLAN ? BEFORE_WHOLE_PLAN : null; }
};

/**
 * Why the chosen plan is out of date, in a sentence a client can act on, or null when it is current. A plan the
 * last re-test did not find says so too (its record carries `outOfDate`).
 */
export function sahChosenOutOfDate(
    plan: SavedStrategy | null | undefined,
    factFind: DraftFactFind | null | undefined,
    savedStrategies: ReadonlyArray<SavedStrategy> | null | undefined,
): string | null {
    if (!plan) return null;
    const then = sahHouseholdOf(plan);
    const now = sahHouseholdNow(factFind, savedStrategies);
    if (then !== now) {
        const who = sahEnteringPerson(factFind);
        const name = String((who === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || '').trim();
        const whose = name ? `${name}'s` : 'the';
        const wasCarried = carried(then);
        const isCarried = carried(now);
        if (isCarried && (!wasCarried || wasCarried === BEFORE_WHOLE_PLAN)) return `Chosen before ${whose} aged care plan was carried in, so its figures are out of date — open it to choose again.`;
        if (!isCarried) return `Chosen with ${whose} aged care plan carried in, which no longer applies, so its figures are out of date — open it to choose again.`;
        return `Chosen with a different aged care plan for ${name || 'the move'}, so its figures are out of date — open it to choose again.`;
    }
    return outOfDateOf(plan)?.reason ?? null;
}
